import Promise from 'bluebird';
import React from 'react';
import { extend, isEmpty, bind, map } from 'underscore';
import { endsWith } from 'underscore.string';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { evaluateRSAAdCustomizerRules, parseRule } from './parser';
import RSAAdCustomizerEditor from './editor';

const pickerRSAAdCustomizerInsert = '{CUSTOMIZER.}';
const pickerRSAAdCustomizerCursorPosition = pickerRSAAdCustomizerInsert.length - 1;
export class RSAAdCustomizerAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      i18n, instrumentation, edmCampaign, jsDataCampaign, permissions, scope,
    }, controller,
  }) {
    super({
      name: 'rsa-adcustomizer',
      priority: 5000,
      i18n,
      activityName: 'RSAAdCustomizer',
      instrumentation,
      scope,
      controller,
    });
    this.edmCampaign = edmCampaign;
    this.jsDataCampaign = jsDataCampaign;
    this.permissions = permissions;
    this.controller = controller;
    this.defaultAnalyzeResult =
      parseRule(pickerRSAAdCustomizerInsert, pickerRSAAdCustomizerCursorPosition);
  }

  getAllAttributes() {
    const { campaignId, adGroupId } = this.scope;
    let adCustomizerAttributes = [];
    return this.jsDataCampaign.AdCustomizerAttribute
      .findAll({
        query: {
          $top: 100,
          $orderby: 'Name asc',
          ...this.permissions && this.permissions.dynamic && this.permissions.dynamic.RSAAdPreviewEnhancement
            && campaignId && adGroupId ? {
              entitySet: this.edmCampaign
                .currentAccount
                .Campaigns
                .$withKey(Number(campaignId))
                .AdGroups
                .$withKey(Number(adGroupId))
                .AdCustomizerAttributes
                .toJSON(),
            } : {},
        },
      })
      .then((returnResult) => {
        if (returnResult.totalCount > 0 || returnResult.length > 0) {
          adCustomizerAttributes = map(returnResult, (item) => {
            const {
              Name, AccountValue, CampaignValue, AdGroupValue, Id, Datatype,
            } = item || {};
            return {
              Name,
              AccountValue,
              CampaignValue,
              AdGroupValue,
              Id,
              Datatype,
            };
          });
        }
        return adCustomizerAttributes;
      });
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    if (!analyzeResult.options) return null;
    const adcustomizerText = `CUSTOMIZER.${this.i18n.getString('RSAAdCustomizer_Attribute_Name')}`;
    const syntax = `${this.i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} {${adcustomizerText}:${this.i18n.getString('KeywordEditor_DefaultText')}}`;
    return (
      <div>
        <span
          className="RSAAdCustomizerEditor-syntax"
        >{syntax}
        </span>
      </div>
    );
  }

  // get menu item
  getMenuItem({
    startIndex, endIndex, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString('Picker_RSA_AdCustomizer_Label'),
      description: this.i18n.getString('Picker_RSA_AdCustomizer_Description'),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(endIndex);
        const newValue = `${beginning}${pickerRSAAdCustomizerInsert}${end}`;
        const suffix = target.value.substring(endIndex);
        this.defaultAnalyzeResult = parseRule(newValue, newValue.length - suffix.length - 1);
        updateAndCloseCallback({
          newValue: `${beginning}${pickerRSAAdCustomizerInsert}${end}`,
          cursorPosition: beginning.length + pickerRSAAdCustomizerCursorPosition,
          isSelectedByUser: true,
        });

        if (this.controller.container) {
          this.controller.updatePicker({
            target,
            syntax: this.getSyntax(this.defaultAnalyzeResult),
            editorView: this.getEditor({
              target,
              analyzeResult: this.defaultAnalyzeResult,
              updateAndCloseCallback,
            }),
            container: this.controller.container,
          });
        }
      },
    };
  }

  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    return !isEmpty(options);
  }

  getEditor({ analyzeResult, updateAndCloseCallback }) {
    const { options } = analyzeResult || {};

    return (<RSAAdCustomizerEditor
      i18n={this.i18n}
      options={options}
      updateAndCloseCallback={updateAndCloseCallback}
      getAttributes={bind(this.getAllAttributes, this)}
    />);
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const rule = parseRule(text, cursorIndex);

    if (rule) {
      const { options: { startIndex } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex, endIndex: cursorIndex, updateAndCloseCallback, target,
          })],
        },
        rule
      ));
    }
    // when not match, see whether to show menu item
    if (text && endsWith(text, '{', cursorIndex)) {
      return Promise.resolve({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: text.length - 1,
          endIndex: cursorIndex,
          updateAndCloseCallback,
          target,
        })],
      });
    }
    return Promise.resolve(null);
  }

  evaluateRule(text) {
    if (text && text.length > 0 && text.search(/{CUSTOMIZER/i) !== -1) {
      return new Promise((resolve) => {
        this.getAllAttributes().then((attributes) => {
          resolve(evaluateRSAAdCustomizerRules(text, attributes));
        });
      });
    }
    return Promise.resolve(text);
  }

  getCharCountExcludingAdCustomizers(text) {
    let charCount = 0;
    let processedText = text || '';
    let toContinue = false;

    let tokens = /\{CUSTOMIZER.[^{}():/"'“‘$*-+=[\]\\:,.]*}/i.exec(processedText);
    if (tokens) {
      processedText = processedText.replace(tokens[0], '');
      toContinue = true;
      charCount = 1;
    } else {
      tokens = /\{CUSTOMIZER.[^{}():/"'“‘$*-+=[\]\\:,.]*:([^}]*)}/i.exec(processedText);
      if (tokens) {
        processedText = processedText.replace(tokens[0], '');
        charCount += tokens[1] ? tokens[1].length : 1;
        toContinue = true;
      }
    }

    return {
      processedText,
      charCount,
      toContinue,
    };
  }

  isValidField() {
    return true;
  }
}
